import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAccessContext } from '../context/AccessContext'
import {
  detectBrowserCapabilities,
  type BrowserCapabilities,
} from '../services/browserCapabilities'
import {
  queryAllPermissions,
  requestPermission,
  hasDeclined,
  type PermissionSubject,
} from '../services/browserPermissions'
import {
  resolveWebPlatformFeatures,
  type BrowserPermissionState,
  type WebFeatureState,
  type WebPlatformFeature,
} from '../services/webPlatformFeatures'
import { fetchWebPushConfig, type WebPushConfig } from '../services/webPushClient'
import {
  installPromptAvailable,
  onInstallAvailabilityChange,
  promptInstall,
  registerWorkerServiceWorker,
  requestBackgroundSync,
  watchInstallAvailability,
} from '../services/workerPwa'
import { readFailedMutations, replayOfflineQueue, readQueueSummary, type OfflineQueueSummary } from '../services/offlineWorkerSync'
import type { QueuedMutation } from '../services/offlineWorkerQueue'

/**
 * Single source of browser-platform truth for the worker web client.
 *
 * It combines three independent things and keeps them independent:
 *   - CAPABILITY: what this browser can physically do (feature detection).
 *   - ENTITLEMENT: what the tenant is commercially allowed (backend-provided).
 *   - PERMISSION: what the person has granted.
 *
 * The frontend uses the combination to decide what to SHOW and what to
 * EXPLAIN. It is never authorization — the backend re-checks every entitlement
 * on every request.
 */

const EMPTY_PERMISSIONS: Record<PermissionSubject, BrowserPermissionState> = {
  geolocation: 'unknown',
  notifications: 'unknown',
  camera: 'unknown',
  microphone: 'unknown',
}

export interface BrowserPlatform {
  capabilities: BrowserCapabilities
  permissions: Record<PermissionSubject, BrowserPermissionState>
  features: Record<WebPlatformFeature, WebFeatureState>
  pushConfig: WebPushConfig
  online: boolean
  installPromptReady: boolean
  queue: OfflineQueueSummary
  /** Failed actions the worker still needs to see. */
  failed: QueuedMutation[]
  declined: (subject: PermissionSubject) => boolean
  refreshPermissions: () => Promise<void>
  refreshQueue: () => Promise<void>
  grant: (subject: PermissionSubject) => Promise<BrowserPermissionState>
  install: () => Promise<'accepted' | 'dismissed' | 'unavailable'>
  syncNow: () => Promise<void>
  isEntitled: (featureKey: string) => boolean
}

const EMPTY_QUEUE: OfflineQueueSummary = { pending: 0, syncing: 0, failed: 0, requiresReview: 0, synced: 0, total: 0 }

export function useBrowserPlatform(): BrowserPlatform {
  const { accessContext } = useAccessContext()
  const [capabilities] = useState<BrowserCapabilities>(() => detectBrowserCapabilities())
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS)
  const [pushConfig, setPushConfig] = useState<WebPushConfig>({ configured: false, entitled: false, publicKey: null })
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine !== false))
  const [installPromptReady, setInstallPromptReady] = useState(() => installPromptAvailable())
  const [queue, setQueue] = useState<OfflineQueueSummary>(EMPTY_QUEUE)
  const [failed, setFailed] = useState<QueuedMutation[]>([])

  const isEntitled = useCallback((featureKey: string) => {
    // Absent entitlement data means "not yet known" — treated as disabled so
    // the UI never advertises a capability the tenant may not have.
    return accessContext?.billingEntitlements?.features?.[featureKey]?.enabled === true
  }, [accessContext])

  const refreshPermissions = useCallback(async () => {
    setPermissions(await queryAllPermissions())
  }, [])

  const refreshQueue = useCallback(async () => {
    setQueue(await readQueueSummary())
    setFailed(await readFailedMutations())
  }, [])

  // Permission state is only QUERIED here, never requested. Prompting without
  // an explicit worker action burns the browser's one-shot permission dialog.
  useEffect(() => { void refreshPermissions() }, [refreshPermissions])
  useEffect(() => { void refreshQueue() }, [refreshQueue])

  useEffect(() => {
    let cancelled = false
    void fetchWebPushConfig().then((config) => { if (!cancelled) setPushConfig(config) })
    return () => { cancelled = true }
  }, [accessContext?.tenantId])

  useEffect(() => registerServiceWorkerOnce(), [])

  useEffect(() => {
    const stopWatching = watchInstallAvailability()
    const stopListening = onInstallAvailabilityChange(setInstallPromptReady)
    return () => { stopWatching(); stopListening() }
  }, [])

  const syncNow = useCallback(async () => {
    await replayOfflineQueue()
    await refreshQueue()
  }, [refreshQueue])

  // Reconnect replay. Background Sync is requested where supported, but the
  // page-side replay below is the guaranteed path — Safari has no Background
  // Sync at all, and parity must not depend on it.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    const goOnline = () => {
      setOnline(true)
      void requestBackgroundSync()
      void syncNow()
    }
    const goOffline = () => setOnline(false)
    const onVisible = () => { if (document.visibilityState === 'visible' && navigator.onLine !== false) void syncNow() }
    const onServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === 'punch-worker-replay-offline-queue') void syncNow()
    }
    window.addEventListener('online', goOnline)
    window.addEventListener('offline', goOffline)
    document.addEventListener('visibilitychange', onVisible)
    navigator.serviceWorker?.addEventListener?.('message', onServiceWorkerMessage)
    return () => {
      window.removeEventListener('online', goOnline)
      window.removeEventListener('offline', goOffline)
      document.removeEventListener('visibilitychange', onVisible)
      navigator.serviceWorker?.removeEventListener?.('message', onServiceWorkerMessage)
    }
  }, [syncNow])

  const grant = useCallback(async (subject: PermissionSubject) => {
    const state = await requestPermission(subject)
    setPermissions((current) => ({ ...current, [subject]: state }))
    return state
  }, [])

  const install = useCallback(async () => {
    const outcome = await promptInstall()
    setInstallPromptReady(installPromptAvailable())
    return outcome
  }, [])

  const features = useMemo(() => resolveWebPlatformFeatures({
    capabilities,
    isEntitled,
    permissions,
    pushConfigured: pushConfig.configured,
  }), [capabilities, isEntitled, permissions, pushConfig.configured])

  return {
    capabilities: { ...capabilities, online },
    permissions,
    features,
    pushConfig,
    online,
    installPromptReady,
    queue,
    failed,
    declined: hasDeclined,
    refreshPermissions,
    refreshQueue,
    grant,
    install,
    syncNow,
    isEntitled,
  }
}

let serviceWorkerRegistered = false
function registerServiceWorkerOnce(): undefined {
  if (serviceWorkerRegistered) return undefined
  serviceWorkerRegistered = true
  void registerWorkerServiceWorker()
  return undefined
}
